import React, {
  type CSSProperties,
  forwardRef,
  type ReactElement,
  useMemo,
} from 'react';
import {FixedSizeList as List} from 'react-window';

type RenderItemProps<ItemType> = {
  index: number;
  style: CSSProperties;
  data: {
    items: readonly ItemType[];
    renderItem: (item: ItemType, style: CSSProperties) => ReactElement;
  };
};

function RenderItem<ItemType>(props: RenderItemProps<ItemType>) {
  const {
    index,
    style,
    data: {items, renderItem},
  } = props;
  const item = items[index];

  return renderItem(item, style);
}

// react-window's inner "sizer" element is `position: static` and as wide as the
// list, so the absolutely-positioned rows resolve their `width: 100%` against
// the outer scroll container — i.e. they're only ever as wide as the visible
// list, never the scrollable content. We override the inner element to be the
// rows' containing block (`position: relative`) and widen it to the content
// width, so every row fills the full scrollable width. That's what lets a
// selected/hover highlight span the entire row (aligned right edge) instead of
// stopping at the end of each row's variable-length text.
const makeInnerElementType = (contentWidth: number | undefined) =>
  forwardRef<HTMLDivElement, {style: CSSProperties}>(function Inner(
    {style, ...rest},
    ref
  ) {
    return (
      <div
        ref={ref}
        style={{
          ...style,
          position: 'relative',
          width:
            contentWidth != null ? `max(100%, ${contentWidth}px)` : style.width,
        }}
        {...rest}
      />
    );
  });

type ImportPanelListProps<ItemType> = {
  ['data-test']?: string;
  items: readonly ItemType[];
  itemSize: number;
  height: number;
  width: number;
  // Width of the widest row's content. When provided (and wider than the list),
  // every row stretches to this width so highlights fill the whole row even
  // while the list is scrolled horizontally.
  contentWidth?: number;
  renderItem: (item: ItemType, style: CSSProperties) => ReactElement;
};

export function ImportPanelList<ItemType>(
  props: ImportPanelListProps<ItemType>
) {
  const itemData = useMemo(
    () => ({
      items: props.items,
      renderItem: props.renderItem,
    }),
    [props.items, props.renderItem]
  );

  const innerElementType = useMemo(
    () => makeInnerElementType(props.contentWidth),
    [props.contentWidth]
  );

  return (
    <div data-test={props['data-test']}>
      <List
        height={props.height}
        itemCount={props.items.length}
        width={props.width}
        itemSize={props.itemSize}
        itemData={itemData}
        innerElementType={innerElementType}>
        {RenderItem<ItemType>}
      </List>
    </div>
  );
}
